/**
 * Pixel Per Inch
 *
 * Panels are sized in inches and are based on the standard badge sizes. We
 * convert the inches to pixels for the canvas.
 */
export const PIXEL_PER_INCH = 96

/**
 * Maximum number of user-created layers allowed on a single badge panel.
 *
 * This does not count the base layer, which always exists and cannot be
 * removed, so a panel holds at most `MAX_BADGE_LAYERS + 1` children.
 *
 * Note: Konva warns on the console once a Stage holds more than 5 layers
 * (`MAX_LAYERS_NUMBER` in konva/lib/Stage.js). That is a performance
 * recommendation, not an enforced limit — the layer is still added and drawn.
 * If layer counts ever become a real performance problem, the fix is to
 * collapse the render-once print path (`BadgePreview`) down to a single Konva
 * layer using groups, rather than to lower this number: separate canvases only
 * earn their keep in the interactive editor.
 */
export const MAX_BADGE_LAYERS = 15

/**
 * Executivevents' Terms & Conditions, which exhibitors agree to on every purchase.
 *
 * Deliberately a link out to the live page rather than copy held in the app, so the
 * current terms are always what's shown without needing a release.
 *
 * Points at the events site rather than executivevents.com/legal, which was the wrong
 * destination (EXEC-1590) — the client supplied this URL as the one exhibitors agree to.
 */
export const TERMS_AND_CONDITIONS_URL = 'https://www.eventstack.co/policies'

/**
 * Countries that require the GDPR popup to be displayed.
 */
export const GDPR_COUNTRIES = [
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Czechia',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Ireland',
  'Italy',
  'Latvia',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'The Netherlands',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
  'United Kingdom',
  'United Kingdom of Great Britain and Northern Ireland',
]

/**
 * Base attendee fields that will always be available.
 */
export const NON_SYNCED_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'title',
  'companyName',
  'phoneNumber',
  'address1',
  'address2',
  'city',
  'state',
  'country',
  'zipCode',
  'categoryName',
  'confirmationCode',
  'attendeeNotes',
]

export const BASE_RECIPIENT_FIELDS = [
  'recipientFirstName',
  'recipientLastName',
  'recipientEmail',
  'defaultLoginUrl',
  'exhibitorLoginUrl',
  'fetchLoginUrl',
]

/**
 * Available merge fields for each email template context.
 */
export const CONTEXT_MERGE_FIELDS = {
  Event: [
    'eventName',
    'eventLocation',
    'eventStartDate',
    'eventEndDate',
    'eventDateRange',
    'eventTechNotes',
  ],
  Organizer: ['organizerName'],
  'Event Exhibitor': [
    'exhibitorName',
    'exhibitorContactFirstName',
    'exhibitorContactLastName',
    'exhibitorContactEmail',
    'exhibitorTasksCompleted',
    'exhibitorTasksIncomplete',
    'exhibitorTasksTotal',
    'exhibitorTotalLicenses',
    'exhibitorAssignedLicenses',
    'exhibitorUnassignedLicenses',
  ],
  Attendee: ['attendeeFirstName', 'attendeeLastName', 'attendeeCompanyName', 'attendeeEmail'],
  'Assigned By': ['assignedByFirstName', 'assignedByLastName', 'assignedByEmail'],
  'Scanned By': ['scannedByFirstName', 'scannedByLastName', 'scannedByEmail'],
  Transaction: ['transactionLicenseCount', 'transactionRentalDeviceCount'],
  'Get Started': ['getStartedURL'],
  License: ['licenseId', 'fetchDeepLink'],
}

export const STOVA_STATUSES = [
  {
    name: 'Confirmed',
    id: 'Confirmed',
  },
  {
    name: 'Attended',
    id: 'Attended',
  },
  {
    name: 'No Show',
    id: 'No Show',
  },
  {
    name: 'Cancelled',
    id: 'Cancelled',
  },
  {
    name: 'Activated',
    id: 'Activated',
  },
  {
    name: 'Waitlist',
    id: 'Waitlist',
  },
  {
    name: 'Declined',
    id: 'Declined',
  },
  {
    name: 'Incomplete',
    id: 'Incomplete',
  },
  {
    name: 'Inactive',
    id: 'Inactive',
  },
  {
    name: 'In progress',
    id: 'In progress',
  },
]

export const CVENT_STATUSES = [
  {
    name: 'Accepted',
    id: 'Accepted',
  },
  {
    name: 'Cancelled',
    id: 'Cancelled',
  },
  {
    name: 'Declined',
    id: 'Declined',
  },
  {
    name: 'Denied',
    id: 'Denied',
  },
  {
    name: 'Invited',
    id: 'Invited',
  },
  {
    name: 'No Response',
    id: 'No Response',
  },
  {
    name: 'Pending Approval',
    id: 'Pending Approval',
  },
  {
    name: 'Visited',
    id: 'Visited',
  },
  {
    name: 'Waitlisted',
    id: 'Waitlisted',
  },
]

export const OPERATOR_OPTIONS = [
  { label: 'Is', id: 'is' },
  { label: 'Is Not', id: 'is_not' },
  { label: 'Contains', id: 'contains' },
  { label: 'Does Not Contain', id: 'not_contains' },
  { label: 'Populated', id: 'populated' },
  { label: 'Not Populated', id: 'not_populated' },
]

// Merge fields whose attendee value is a list (e.g. attendee.sessions is an
// array of session ids). For these, `contains` is the set-membership
// operator ("attendee's list contains this id") and shows the option
// picker even though it's a contains-family operator. `is` / `is_not` are
// scalar by intent — they don't have a coherent meaning against an array
// in Trackr's evaluator and shouldn't be used for these fields.
export const MULTI_VALUE_MERGE_FIELDS = ['sessions']

export const INCLUSION_OPTIONS = [
  { label: 'AND', id: 'AND' },
  { label: 'OR', id: 'OR' },
]

/**
 * API error codes
 */
export const ERROR_CODES = {
  BOOTH_STAFF_ONLY: 'booth_staff_only',
}

/**
 * User-facing error messages
 */
export const ERROR_MESSAGES = {
  BOOTH_STAFF_ONLY:
    "You don't have access to the portal. Please use the Fetch mobile app instead.",
}

/**
 * Standardized event emails that EE staff can resend to licensed users.
 * Identifiers match the API's email template identifiers.
 */
export const RESENDABLE_EMAILS = [
  { identifier: 'fetch-kbyg-licensees', label: 'Welcome' },
  { identifier: 'fetch-license-assigned', label: 'License Assigned' },
]

/**
 * Display labels for sent-email history entries, keyed by template identifier.
 */
export const EMAIL_IDENTIFIER_LABELS = {
  'fetch-kbyg-licensees': 'Welcome',
  'fetch-license-assigned': 'License Assigned',
  'attendees-scan-and-go': 'Scan and Go',
  'exhibitor-event-reminder': 'Event Reminder',
}
